---
title: Workspaces
lede: A workspace is a set of repositories you work on together. Every session belongs to one.
order: 5
part: Get started
---

A workspace groups repositories that belong together, such as a front end and its API. Every session belongs to one workspace and works on a copy of its repositories.

### Switching workspaces

Click the workspace name at the top left of the window, or press `⌃⇧1` … `⌃⇧9`. Each row shows how many sessions are waiting for you or open. **Workspace settings** and **New workspace** are at the foot.

![The workspace menu](media/workspace-menu.webp)

### What a workspace decides

- Its [repositories](/repositories) and how each installs, runs and is checked.
- The [targets](/targets) a session can start on, and what its agents keep in [memory](/memory).
- The [session image](/session-image) and [where sessions run](/placement).
- What sessions may reach: [network](/network), [services on this computer](/services), [tunnels](/tunnels).
- Session defaults: model, effort, source branch, autonomy, the agent's tools.
- How work leaves: see [Merging](/merging).

Each setting starts from the application's value; override it in [Workspace settings](/workspace-settings).

### Creating a workspace

**New workspace** opens a wizard.

1. **Workspace**: give it a name and a colour.
2. **Repositories**: **Add folders** takes one or more folders. A git repository is added as is; any other folder is searched for repositories. Airlock fills in each one's commands and port; edit them if needed.
3. Press **Create workspace**. The workspace now exists.

The next steps are optional, each saved by **Continue** or passed by **Skip this**:

| Step | What it asks |
|---|---|
| Session image | The toolchains Airlock chose for your repositories |
| First install | Runs each install once, so the first session starts faster |
| Network | The hosts your code needs. Everything else is refused |
| Services | A database or cache on this computer the repositories use |
| Public URLs | Whether to publish a port to the internet |

**Ready** ends the wizard with **Open the workspace**. **Finish later** leaves it at any point after creation: the workspace works on defaults, and every step is also a page in Workspace settings. The Network step waits for the first install, since it learns from it; **Continue anyway** skips the wait.

### Name, colour and deletion

**Workspace settings › General** holds the name and the colour, which marks the workspace and its sessions.

![Workspace general settings](media/ws-general.webp)

**Delete workspace** › **Delete** removes its sessions, sandboxes, settings and tasks. The repositories on your computer are untouched.
